// What the matchers mean by a word character, a capital and a shouted headline, and the small text
// helpers they share (atlas-geo `text.rs`). ASCII only: the headline tables were written for
// JavaScript's `RegExp` without the `u` flag, where `\w` and `\b` are ASCII.

/** JavaScript's `\w` without `u`: ASCII letters, digits and `_`. */
export function isWordCode(c: number): boolean {
  return (c >= 48 && c <= 57) || (c >= 65 && c <= 90) || (c >= 97 && c <= 122) || c === 95;
}

export function isUpperCode(c: number): boolean {
  return c >= 65 && c <= 90;
}

/** JavaScript's `\s`: tab, line terminators, the Unicode space separators and U+FEFF. */
export function isSpaceCode(c: number): boolean {
  return (
    c === 32 ||
    (c >= 9 && c <= 13) ||
    c === 0xa0 ||
    c === 0x1680 ||
    (c >= 0x2000 && c <= 0x200a) ||
    c === 0x2028 ||
    c === 0x2029 ||
    c === 0x202f ||
    c === 0x205f ||
    c === 0x3000 ||
    c === 0xfeff
  );
}

/** Lower-cases ASCII letters only, so every index still points at the same character. */
export function asciiLower(s: string): string {
  return s.replace(/[A-Z]+/g, (m) => m.toLowerCase());
}

/** More than six tenths of a headline's letters (at least 12 of them) in capitals: its casing says
 * nothing, so no rule that reads capitals applies (`shouted` in atlas-geo `headline.rs`). */
export function shouted(s: string): boolean {
  let letters = 0;
  let upper = 0;
  for (let i = 0; i < s.length; i++) {
    const c = s.charCodeAt(i);
    if (c >= 65 && c <= 90) {
      letters++;
      upper++;
    } else if (c >= 97 && c <= 122) letters++;
  }
  return letters >= 12 && upper * 10 > letters * 6;
}

const ENTITIES: Record<string, string> = {
  amp: "&",
  quot: '"',
  apos: "'",
  "#39": "'",
  "#x27": "'",
  nbsp: " ",
  lt: "<",
  gt: ">",
};

/** A headline as the matchers read it: the few HTML entities feeds leave in decoded, typographic
 * apostrophes and hyphens made plain, trimmed. */
export function clean(text: string): string {
  return text
    .replace(/&(amp|quot|apos|#39|#x27|nbsp|lt|gt);/gi, (whole, name: string) => ENTITIES[name.toLowerCase()] ?? whole)
    .replace(/[‘’ʼ′]/g, "'")
    .replace(/[“”]/g, '"')
    .replace(/[‐‑]/g, "-")
    .trim();
}

export function escapeRe(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/** A literal name as a whole-word pattern: white space in the name matches any run of white space
 * and hyphens, `&` takes optional spaces around it, and no word character may touch either end. */
export function phraseSource(name: string): string {
  const body = name
    .trim()
    .split(/\s+/)
    .map((piece) => escapeRe(piece).replace(/&/g, "\\s*&\\s*"))
    .join("[\\s-]+");
  return `(?<!\\w)${body}(?!\\w)`;
}

/** The first run of word characters, lower-case: what the first-word indexes key on. */
export function headOf(name: string): string {
  const m = /\w+/.exec(name);
  return m ? m[0].toLowerCase() : "";
}

/** Each run of word characters in an already lower-cased text, once. */
export function wordsOf(lower: string): Set<string> {
  const out = new Set<string>();
  for (const m of lower.matchAll(/\w+/g)) out.add(m[0]);
  return out;
}
